import React, { useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { styleReset } from 'react95';
import { createGlobalStyle } from 'styled-components';
import original from 'react95/dist/themes/original';
import ms_sans_serif from 'react95/dist/fonts/ms_sans_serif.woff2';
import ms_sans_serif_bold from 'react95/dist/fonts/ms_sans_serif_bold.woff2';
import PaymentArrangementForm from './PaymentArrangementForm';
import { Button, Window, WindowHeader, WindowContent, GroupBox } from 'react95';

const GlobalStyles = createGlobalStyle`
  ${styleReset}
  @font-face {
    font-family: 'MS Sans Serif';
    src: url('${ms_sans_serif}') format('woff2');
    font-weight: 400;
    font-style: normal;
  }
  @font-face {
    font-family: 'MS Sans Serif';
    src: url('${ms_sans_serif_bold}') format('woff2');
    font-weight: bold;
    font-style: normal;
  }
  body, input, select, textarea {
    font-family: 'MS Sans Serif', Arial, sans-serif;
  }
`;

/**
 * Demo component for PaymentArrangementForm
 * 
 * Demonstrates:
 * - Creating new payment arrangements
 * - Editing existing arrangements
 * - Frequency and amount scheduling
 * - Due date calculations
 * - Settlement options
 * - Payment schedule generation
 */
const PaymentArrangementFormDemo = () => {
  const [showForm, setShowForm] = useState(false);
  const [selectedArrangement, setSelectedArrangement] = useState(null);
  const [savedArrangements, setSavedArrangements] = useState([]);

  // Sample client ID for demo
  const demoClientId = 'client_demo_123';

  // Sample existing arrangements
  const sampleArrangements = [
    {
      id: 'arrangement_1',
      clientId: demoClientId,
      frequency: 'monthly',
      amount: 250.00,
      nextDueDate: new Date('2026-02-15'),
      settleForLess: true,
      settleAmount: 5000.00,
      arrears: 500.00,
      isActive: true,
      createdAt: new Date('2025-12-01'),
      updatedAt: new Date('2026-01-10')
    },
    {
      id: 'arrangement_2',
      clientId: demoClientId,
      frequency: 'bi-weekly',
      amount: 125.00,
      nextDueDate: new Date('2026-01-28'),
      settleForLess: false,
      settleAmount: 0,
      arrears: 0,
      isActive: true,
      createdAt: new Date('2026-01-01'),
      updatedAt: new Date('2026-01-01')
    },
    {
      id: 'arrangement_3',
      clientId: demoClientId,
      frequency: 'weekly',
      amount: 75.00,
      nextDueDate: new Date('2025-12-20'),
      settleForLess: false,
      settleAmount: 0,
      arrears: 1200.00,
      isActive: false,
      createdAt: new Date('2025-10-01'),
      updatedAt: new Date('2025-12-15')
    }
  ];

  const handleNewArrangement = () => {
    setSelectedArrangement(null);
    setShowForm(true);
  };

  const handleEditArrangement = (arrangement) => {
    setSelectedArrangement(arrangement);
    setShowForm(true);
  };

  const handleArrangementSaved = (arrangement) => {
    console.log('Arrangement saved:', arrangement);
    setSavedArrangements(prev => {
      const existing = prev.findIndex(a => a.id === arrangement.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = arrangement;
        return updated;
      }
      return [...prev, arrangement];
    });
  };

  const handleClose = () => {
    setShowForm(false);
    setSelectedArrangement(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <ThemeProvider theme={original}>
      <GlobalStyles />
      <div style={{ 
        padding: '20px', 
        backgroundColor: '#008080',
        minHeight: '100vh'
      }}>
        <Window style={{ width: '900px', margin: '0 auto' }}>
          <WindowHeader>
            <span>📅 Payment Arrangement Form - Demo</span>
          </WindowHeader>
          <WindowContent>
            <div style={{ padding: '20px' }}>
              <h2 style={{ marginBottom: '20px', fontSize: '16px' }}>Payment Arrangement Management Demo</h2>
              
              <GroupBox label="Demo Controls" style={{ marginBottom: '20px' }}>
                <div style={{ padding: '15px' }}>
                  <Button onClick={handleNewArrangement} style={{ marginRight: '10px' }}>
                    ➕ New Arrangement
                  </Button>
                  <p style={{ marginTop: '10px', fontSize: '11px', color: '#666' }}>
                    Click to create a new payment arrangement for demo client
                  </p>
                </div>
              </GroupBox>

              <GroupBox label="Sample Arrangements" style={{ marginBottom: '20px' }}>
                <div style={{ padding: '15px' }}>
                  <p style={{ marginBottom: '10px', fontSize: '11px' }}>
                    Click on any arrangement to edit it:
                  </p>
                  {sampleArrangements.map((arrangement, index) => (
                    <div 
                      key={arrangement.id}
                      onClick={() => handleEditArrangement(arrangement)}
                      style={{
                        padding: '10px',
                        marginBottom: '10px',
                        border: '1px solid #c0c0c0',
                        backgroundColor: arrangement.isActive ? '#f8fff8' : '#f8f8f8',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0ff'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = arrangement.isActive ? '#f8fff8' : '#f8f8f8'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <strong style={{ fontSize: '12px' }}>Arrangement #{index + 1}</strong>
                        <span style={{ 
                          padding: '2px 6px',
                          fontSize: '10px',
                          backgroundColor: arrangement.isActive ? '#006600' : '#666',
                          color: 'white',
                          borderRadius: '3px'
                        }}>
                          {arrangement.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                        <div>
                          <strong>Amount:</strong> {formatCurrency(arrangement.amount)}<br/>
                          <strong>Frequency:</strong> {arrangement.frequency}
                        </div>
                        <div>
                          <strong>Next Due:</strong> {new Date(arrangement.nextDueDate).toLocaleDateString()}<br/>
                          {arrangement.arrears > 0 && (
                            <span style={{ color: '#cc0000' }}>
                              <strong>Arrears:</strong> {formatCurrency(arrangement.arrears)}
                            </span>
                          )}
                        </div>
                        <div>
                          {arrangement.settleForLess && (
                            <>
                              <strong>Settlement:</strong> {formatCurrency(arrangement.settleAmount)}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </GroupBox>

              {savedArrangements.length > 0 && (
                <GroupBox label="Recently Saved Arrangements">
                  <div style={{ padding: '15px' }}>
                    <p style={{ marginBottom: '10px', fontSize: '11px', color: '#006600' }}>
                      ✅ Arrangements saved during this demo session:
                    </p>
                    {savedArrangements.map((arrangement, index) => (
                      <div 
                        key={arrangement.id}
                        style={{
                          padding: '8px',
                          marginBottom: '8px',
                          border: '1px solid #00aa00',
                          backgroundColor: '#e0ffe0',
                          fontSize: '11px'
                        }}
                      >
                        <strong>Saved #{index + 1}:</strong> {formatCurrency(arrangement.amount)} {arrangement.frequency} 
                        - Next due: {new Date(arrangement.nextDueDate).toLocaleDateString()}
                      </div>
                    ))}
                  </div>
                </GroupBox>
              )}

              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#ffffcc', border: '1px solid #cccc00' }}>
                <h3 style={{ fontSize: '12px', marginBottom: '10px' }}>💡 Demo Features:</h3>
                <ul style={{ fontSize: '11px', marginLeft: '20px' }}>
                  <li>Create new payment arrangements with frequency and amount</li>
                  <li>Edit existing arrangements (click on sample arrangements above)</li>
                  <li>Automatic due date calculation based on frequency</li>
                  <li>Track arrears for missed payments</li>
                  <li>Settlement options with custom amounts</li>
                  <li>Generate payment schedules for planning</li>
                  <li>Active/inactive status management</li>
                  <li>Real-time validation and error handling</li>
                </ul>
              </div>
            </div>
          </WindowContent>
        </Window>

        {/* Payment Arrangement Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000
          }}>
            <PaymentArrangementForm
              clientId={demoClientId}
              initialArrangement={selectedArrangement}
              onArrangementSaved={handleArrangementSaved}
              onClose={handleClose}
            />
          </div>
        )}
      </div>
    </ThemeProvider>
  );
};

export default PaymentArrangementFormDemo;
